/**
 * Browser cache for `getMasterdata` (services/services.ts).
 *
 * Lists live in sessionStorage for at most an hour. They used to sit in
 * localStorage with no expiry, so a list read before the backend had it
 * seeded stayed `[]` in that browser forever and the select showed nothing.
 *
 * Every storage access is wrapped: Safari private mode and blocked site data
 * throw on access, and a cache that can't be reached should only cost a
 * request, never the list.
 */

export const MASTERDATA_CACHE_PREFIX = "masterdata-";
export const MASTERDATA_CACHE_TTL_MS = 60 * 60 * 1000;

type Entry<T> = { expiresAt: number; data: T[] };

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;

const reach = (getStorage: () => StorageLike): StorageLike | null => {
  try {
    return getStorage();
  } catch {
    return null;
  }
};

const isEntry = (value: unknown): value is Entry<unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  typeof (value as Entry<unknown>).expiresAt === "number" &&
  Array.isArray((value as Entry<unknown>).data);

/**
 * The cached list, or null when there is none worth using. An expired entry,
 * malformed JSON or the old bare-array format is removed so the caller fetches.
 */
export function readMasterdataCache<T>(
  getStorage: () => StorageLike,
  key: string,
  now: number = Date.now(),
): T[] | null {
  const storage = reach(getStorage);
  if (!storage) return null;

  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;

    let parsed: unknown = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      /* fall through to removal */
    }

    if (isEntry(parsed) && parsed.expiresAt > now) return parsed.data as T[];
    storage.removeItem(key);
    return null;
  } catch {
    return null;
  }
}

/**
 * An empty list is never cached. Empty means "not seeded yet" as often as it
 * means "none", and caching it would hide a freshly seeded list for the full
 * TTL. The cost is one small request per render for a list that really is empty.
 */
export function writeMasterdataCache<T>(
  getStorage: () => StorageLike,
  key: string,
  data: T[],
  now: number = Date.now(),
): void {
  if (data.length === 0) return;

  const storage = reach(getStorage);
  if (!storage) return;

  const entry: Entry<T> = { expiresAt: now + MASTERDATA_CACHE_TTL_MS, data };
  try {
    storage.setItem(key, JSON.stringify(entry));
  } catch {
    /* quota or blocked storage: the next call just fetches again */
  }
}

/**
 * Drop the `masterdata-*` keys the old localStorage cache left behind,
 * including the permanent `[]` entries this cache replaces.
 */
export function purgeLegacyMasterdataCache(getStorage: () => StorageLike): void {
  const storage = reach(getStorage);
  if (!storage) return;

  try {
    const stale: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(MASTERDATA_CACHE_PREFIX)) stale.push(key);
    }
    stale.forEach((key) => storage.removeItem(key));
  } catch {
    /* nothing to clean up that we can reach */
  }
}
